<script setup lang="ts">
import type { ProductItemDetail } from '#layers/base/server/utils/product-details/domain'
import type { AlternativesEntry } from '~/composables/useAlternativesEntry'
import { useAlternativesButton } from '~/composables/useAlternativesButton'
import type { CampaignProductItem } from '~/composables/useCampaignProductGroups'
import { getSpeciesPictogramSrc } from '~/utils/speciesPictogram'
import IconAlternative from '~icons/mdi/shopping-search-outline'
import IconExternalLink from '~icons/mdi/open-in-new'
import DownloadIcon from '~icons/mdi/trayArrowDown'

type ProductDetailItem = CampaignProductItem & { detail: ProductItemDetail }

interface Props {
  item: ProductDetailItem
  clinicChain?: { handle: string, name: string, color?: string, group?: string, groupHandle?: string } | null
  productName: string
  supplier?: { handle?: string, name?: string, imageUrl?: string }
  manufacturer?: { handle: string, name: string, imageUrl?: string }
  alternatives: AlternativesEntry | null
  currentPrice?: string
  campaign?: {
    beforePrice: string | number
    afterPrice: string | number
    discount: number
    discount_type: string
    discountPercent?: number
  }
  discountFlagPercent?: number
}

const externalUrlData = {
  ema: {
    name: 'Eurpean Medicines Agency',
    color: '#003399',
    image: 'https://cdn.heiland.com/assets/logo-website-ema.png',
  },
  medvet: {
    name: 'Medvet',
    color: '#27b3b8',
    image: 'https://cdn.heiland.com/assets/logo-website-medvet.png',
  },
  vetidata: {
    name: 'Vetidata',
    color: '#cc0000',
    image: 'https://cdn.heiland.com/assets/logo-website-vetidata.png',
  },
  vetiSearch: {
    name: 'VetiSearch',
    color: '#223060',
    image: 'https://cdn.heiland.com/assets/logo-website-vetisearch.png',
  },
}

const getRestUrlData = (url: string) => {
  try {
    const urlObj = new URL(url)
    const hostname = urlObj.hostname.toLowerCase()

    if (hostname.includes('medicines.health.europa.eu')) {
      return externalUrlData.ema
    }
    else if (hostname.includes('vetidata.de')) {
      return externalUrlData.vetidata
    }
    else if (hostname.includes('vetisearch.de')) {
      return externalUrlData.vetiSearch
    }
    else if (hostname.includes('med-vet.fr')) {
      return externalUrlData.medvet
    }

    return {
      name: 'External Link',
      color: '#666666',
      image: '',
    }
  }
  catch {
    return {
      name: 'External Link',
      color: '#666666',
      image: '',
    }
  }
}

const props = defineProps<Props>()

const localePath = useLocalePath()
const { t, locale } = useI18n()
const searchStore = useSearchStore()
const gtm = useGtm()
const pageSection = usePageSection()
const { searchRouteName } = useSearchRoute()

const detail = computed(() => props.item.detail)
const atcvetCode = computed(() => detail.value.atcvetCode[0])

function trackExploreAlternatives(atcCode?: string) {
  gtm?.trackEvent({
    event: 'explore_product_alternatives',
    page_section: pageSection,
    item_id: props.item.handle,
    item_name: props.productName,
    supplier_name: props.supplier?.name ?? '',
    atc_code: atcCode || atcvetCode.value || '',
  })
}

const { isVisible: showAlternativesBtn, navTo: alternativesNavTo, handleClick: handleAlternativesClick } = useAlternativesButton(
  () => props.alternatives,
  () => trackExploreAlternatives(),
)

async function onAtcCodeClick(code: string) {
  trackExploreAlternatives(code)
  searchStore.setSearchMeta(code, 'atc_code_click')
  await navigateTo({ path: localePath(searchRouteName.value), query: { q: code } })
}

const isFavourite = defineModel<boolean>('isFavourite')

const emit = defineEmits<{
  saved: [payload: { listName: string, isNewList: boolean, quantity: number }]
}>()

function handleSaved(payload: { listName: string, isNewList: boolean, quantity: number }) {
  emit('saved', payload)
}

const productCodeCaption = (code: string) =>
  t(`enum.product_code_type.${code.toLowerCase()}`) || code

const getLocalizedText = (text: string | Record<string, string> | undefined) =>
  typeof text === 'string' ? text : text?.[locale.value]

const description = computed(() =>
  getLocalizedText(detail.value.longDescription)
  ?? getLocalizedText(detail.value.shortDescription)
  ?? '',
)

const admissionSpecies = computed(() =>
  (detail.value.admissionSpecies ?? []).map(key => ({
    key,
    pictogramSrc: getSpeciesPictogramSrc(key),
  })),
)

const imageTemperatureType = computed(() => {
  const temperatureType = detail.value.temperatureType
  return temperatureType === 'CHILLED' || temperatureType === 'FREEZ'
    ? temperatureType
    : null
})
</script>

<template>
  <v-card flat class="rounded-b-0">
    <v-row no-gutters>
      <v-col cols="12" md="4" class="pa-4 h-100 d-flex flex-column">
        <ProductCardImage
          v-model:is-favourite="isFavourite"
          min-height="250"
          max-height="300"
          class="mb-2"
          :scale-pricing="item.scalePricing"
          :product="item"
          :img-src="item.imageUrl"
          :fallback-src="supplier?.imageUrl"
          :alt="productName"
          :campaign="campaign"
          :current-price="currentPrice"
          :temperature-type="imageTemperatureType"
          :discount-flag-percent="discountFlagPercent"
          @saved="handleSaved"
        />

        <div v-if="detail.attachments.length > 0" class="mt-auto pt-4">
          <template v-for="attachment in detail.attachments" :key="attachment.type">
            <v-btn
              v-if="attachment.type === 'INFORMATION_BROCHURE' || attachment.type === 'PATIENT_INFORMATION_LEAFLET'"
              block
              variant="outlined"
              color="grey-darken-1"
              class="d-flex justify-space-between mb-3"
              tag="a"
              :href="attachment.url"
              download
              target="_blank"
              size="large"
              data-track-id="product_download_attachment"
              :append-icon="DownloadIcon"
            >
              {{ $t(`productDetail.${attachment.type}`) }}
            </v-btn>
          </template>
        </div>

        <v-btn
          v-if="showAlternativesBtn"
          data-nav-id="product_detail_alternatives"
          class="d-flex justify-space-between"
          variant="outlined"
          color="grey-darken-1"
          data-test-id="product-alternativeBtn"
          size="large"
          :to="alternativesNavTo"
          @click="handleAlternativesClick"
        >
          {{ $t('product.alternative.button') }}

          <template #append>
            <v-icon size="large">
              <IconAlternative />
            </v-icon>
          </template>
        </v-btn>
        <template v-for="externalUrl in detail.externalUrls" :key="externalUrl">
          <v-btn
            class="d-flex align-center justify-space-between mt-2"
            variant="outlined"
            :color="getRestUrlData(externalUrl).color"
            size="large"
            tag="a"
            :href="externalUrl"
            target="_blank"
            data-nav-id="product_detail_external_partner"
          >
            {{ getRestUrlData(externalUrl).name }}
            <template #append>
              <NuxtImg
                :src="getRestUrlData(externalUrl).image"
                width="85"
                height="30"
              />
            </template>
          </v-btn>
        </template>
      </v-col>

      <v-col cols="12" md="8" class="pa-4">
        <ProductHeadlineAndInfo
          :clinic-chain="clinicChain"
          :id-external="item.idExternal"
          :product-name="productName"
          :manufacturer="manufacturer"
          :supplier="supplier"
        />

        <v-row class="mb-4 mt-4">
          <v-col v-for="code in detail.productCodes" :key="code.type" cols>
            <div class="text-caption text-grey mb-2">
              {{ productCodeCaption(code.type) }}
            </div>
            <div>{{ code.value.length > 0 ? code.value : '-' }}</div>
          </v-col>
        </v-row>

        <v-row class="border-t-sm border-b-sm product-detail__list-grid">
          <v-col cols>
            <div class="text-subtitle-2 font-weight-bold mb-2 text-grey">
              {{ $t('productDetail.atcvetCode') }}
            </div>
            <div v-if="atcvetCode">
              <NuxtLink
                :to="{ path: localePath(searchRouteName), query: { q: atcvetCode } }"
                class="d-flex align-center"
                data-nav-id="product_atcvet_code"
                @click.prevent="onAtcCodeClick(atcvetCode)"
              >
                {{ atcvetCode }}
                <IconExternalLink class="ml-1 " />
              </NuxtLink>
            </div>
          </v-col>
          <v-col cols>
            <div class="text-subtitle-2 font-weight-bold mb-2 text-grey">
              {{ $t('filterOptions.ingredients') }}
            </div>
            <ul>
              <li
                v-for="activeIngredient in detail.activeIngredients"
                :key="activeIngredient"
                class="mb-2"
              >
                {{ $t(activeIngredient) }}
              </li>
            </ul>
          </v-col>
          <v-col cols>
            <div class="text-subtitle-2 font-weight-bold mb-2 text-grey">
              {{ $t('filterOptions.admissionSpecies') }}
            </div>
            <ul>
              <li
                v-for="species in admissionSpecies"
                :key="species.key"
                class="d-flex align-center mb-2"
              >
                <v-img
                  v-if="species.pictogramSrc"
                  :src="species.pictogramSrc"
                  alt=""
                  width="18"
                  height="18"
                  inline
                  eager
                  class="mr-1 flex-shrink-0"
                />
                <span>{{ $t(species.key) }}</span>
              </li>
            </ul>
          </v-col>
          <v-col cols>
            <div class="text-subtitle-2 font-weight-bold mb-2 text-grey">
              {{ $t('filterOptions.dosageForms') }}
            </div>
            <ul>
              <li v-for="dosageForm in detail.dosageForms" :key="dosageForm" class="mb-2">
                {{ $t(dosageForm) }}
              </li>
            </ul>
          </v-col>
          <v-col cols>
            <div class="text-subtitle-2 font-weight-bold mb-2 text-grey">
              {{ $t('filterOptions.typeOfApplications') }}
            </div>
            <ul>
              <li v-for="application in detail.typeOfApplications" :key="application" class="mb-2">
                {{ $t(application) }}
              </li>
            </ul>
          </v-col>
          <v-col cols>
            <div class="text-subtitle-2 font-weight-bold mb-2 text-grey">
              {{ $t('filterOptions.saleDelimitations') }}
            </div>
            <ul>
              <li
                v-for="delimitation in detail.saleDelimitations"
                :key="delimitation"
                class="mb-2"
              >
                {{ $t(delimitation) }}
              </li>
            </ul>
          </v-col>
        </v-row>

        <div class="mt-6">
          <div class="text-h6 mb-2 font-weight-bold">
            {{ $t('productDetail.description') }}
          </div>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="product-description text-body-1" v-html="description" />
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<style scoped lang="scss">
.product-detail {
  &__list-grid {
    > div {
      margin-block: .5rem;

      &:not(:first-child) {
        border-left: 1px solid #e0e0e0;
      }
    }
  }
}

.v-card {
  border-radius: 8px;
}

ul {
  list-style-type: none;
  padding-left: 0;
}

li {
  margin-bottom: 4px;
}

.v-btn {
  height: 44px;
  text-transform: none;
  letter-spacing: normal;
}

.product-description {
  :deep(h1) {
    display: block;
    font-size: 2rem;
    font-weight: bold;
    margin: 0.67em 0;
  }

  :deep(h2) {
    display: block;
    font-size: 1.5rem;
    font-weight: bold;
    margin: 0.83em 0;
  }

  :deep(h3) {
    display: block;
    font-size: 1.17rem;
    font-weight: bold;
    margin: 1em 0;
  }

  :deep(h4) {
    display: block;
    font-size: 1rem;
    font-weight: bold;
    margin: 1.33em 0;
  }

  :deep(h5) {
    display: block;
    font-size: 0.83rem;
    font-weight: bold;
    margin: 1.67em 0;
  }

  :deep(h6) {
    display: block;
    font-size: 0.67rem;
    font-weight: bold;
    margin: 2.33em 0;
  }

  :deep(p) {
    display: block;
    margin: 1em 0;
  }

  :deep(ol),
  :deep(ul) {
    display: block;
    padding-left: 1.5em;
    margin: 1em 0;
  }

  :deep(ol > li),
  :deep(ul > li) {
    list-style-type: none;
  }

  :deep(li) {
    display: list-item;
  }

  :deep(li::before) {
    display: inline-block;
    white-space: nowrap;
    width: 1.2em;
    margin-left: -1.5em;
    margin-right: 0.3em;
    text-align: right;
  }

  :deep(ul > li::before) {
    content: '\2022';
  }

  :deep(ol li) {
    counter-increment: list-0;
    counter-reset: list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8;
  }

  :deep(ol li::before) {
    content: counter(list-0, decimal) '. ';
  }

  :deep(ol li.ql-indent-1) {
    counter-increment: list-1;
    counter-reset: list-2 list-3 list-4 list-5 list-6 list-7 list-8;
  }

  :deep(ol li.ql-indent-1::before) {
    content: counter(list-1, lower-alpha) '. ';
  }

  :deep(ol li.ql-indent-2) {
    counter-increment: list-2;
    counter-reset: list-3 list-4 list-5 list-6 list-7 list-8;
  }

  :deep(ol li.ql-indent-2::before) {
    content: counter(list-2, lower-roman) '. ';
  }

  :deep(ol li.ql-indent-3) {
    counter-increment: list-3;
    counter-reset: list-4 list-5 list-6 list-7 list-8;
  }

  :deep(ol li.ql-indent-3::before) {
    content: counter(list-3, decimal) '. ';
  }

  :deep(ol li.ql-indent-4) {
    counter-increment: list-4;
    counter-reset: list-5 list-6 list-7 list-8;
  }

  :deep(ol li.ql-indent-4::before) {
    content: counter(list-4, lower-alpha) '. ';
  }

  :deep(ol li.ql-indent-5) {
    counter-increment: list-5;
    counter-reset: list-6 list-7 list-8;
  }

  :deep(ol li.ql-indent-5::before) {
    content: counter(list-5, lower-roman) '. ';
  }

  :deep(ol li.ql-indent-6) {
    counter-increment: list-6;
    counter-reset: list-7 list-8;
  }

  :deep(ol li.ql-indent-6::before) {
    content: counter(list-6, decimal) '. ';
  }

  :deep(ol li.ql-indent-7) {
    counter-increment: list-7;
    counter-reset: list-8;
  }

  :deep(ol li.ql-indent-7::before) {
    content: counter(list-7, lower-alpha) '. ';
  }

  :deep(ol li.ql-indent-8) {
    counter-increment: list-8;
  }

  :deep(ol li.ql-indent-8::before) {
    content: counter(list-8, lower-roman) '. ';
  }

  @for $i from 1 through 8 {
    :deep(.ql-indent-#{$i}) {
      padding-left: ($i * 3) * 1em;
    }
  }

  :deep(dl) {
    display: block;
    margin: 1em 0;
  }

  :deep(dt) {
    display: block;
    font-weight: bold;
  }

  :deep(dd) {
    display: block;
    margin-left: 40px;
  }
}
</style>
